import { ANNUAL_DISCOUNT_RATE, LAUNCH_PLANS } from '@gsi/billing/catalogue';

/**
 * The price list, read from the commercial catalogue.
 *
 * Not retyped into a marketing component, which is the point (§25): the
 * catalogue is what `scripts/apply-catalogue.ts` writes to the database and
 * what Stripe eventually charges, so a page that keeps its own copy of "449 €"
 * is a page that will one day disagree with an invoice. A customer discovers
 * that disagreement, not us.
 *
 * Imported through `@gsi/billing/catalogue` rather than the package root: the
 * root barrel pulls in the entitlement service, which pulls in `@gsi/db` and
 * drizzle. The catalogue module imports nothing but types, so it erases to
 * pure data in the browser bundle.
 *
 * What this file does add is **French**. The catalogue speaks in keys —
 * `included_minutes`, `payments_enabled` — because a key is what code branches
 * on. Turning those into a sentence a salon owner reads is presentation, and
 * it belongs here.
 */

export interface PublicPlan {
  readonly code: string;
  readonly name: string;
  readonly summary: string;
  /** Monthly, excluding VAT, already formatted. Null when the plan is quote-only. */
  readonly monthly: string | null;
  readonly annualPerMonth: string | null;
  readonly setupFee: string | null;
  /** What a minute beyond the bundle costs, already formatted. */
  readonly overagePerMinute: string | null;
  /** What it includes today: quotas first, then features. */
  readonly features: readonly string[];
  /** What it is sold with that is not built yet, said as such (COMING_SOON). */
  readonly upcoming: readonly string[];
}

/**
 * The annual commitment, in the words a salon owner uses.
 *
 * Derived, never asserted. The catalogue's discount is a rate; "deux mois
 * offerts" is a sentence somebody writes on a slide and nobody re-checks when
 * the rate changes. At the launch rate this is a little over one month, and
 * saying so is the difference between a price list and a pitch.
 */
export function annualSaving(): { readonly percent: string; readonly months: string } {
  const months = ANNUAL_DISCOUNT_RATE * 12;
  return {
    percent: new Intl.NumberFormat(LOCALE, { style: 'percent' }).format(ANNUAL_DISCOUNT_RATE),
    months: new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 }).format(months),
  };
}

const LOCALE = 'fr-FR';

function euros(amountMinor: number): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
  }).format(amountMinor / 100);
}

/** What a feature flag means to somebody deciding whether to buy. */
const FEATURE_LABELS: Readonly<Record<string, string>> = {
  payments_enabled: 'Paiement à la réservation, par lien sécurisé',
  analytics_detailed: 'Statistiques détaillées par motif d’appel',
  analytics_advanced: 'Export de l’historique des appels (CSV)',
  revenue_tracking: 'Valeur des réservations par appel',
  multi_location: 'Plusieurs établissements',
  advanced_workflows: 'Workflows avancés',
  advanced_integrations: 'Intégrations poussées',
  group_management: 'Administration de groupe',
  priority_support: 'Support prioritaire',
  custom_voice: 'Choix de la voix',
  api_access: 'Accès API',
  custom_integrations: 'Connecteurs sur mesure',
  sla: 'Engagement de service contractuel',
};

/** A feature key in the words of somebody deciding whether to buy, or null. */
export function featureLabel(key: string): string | null {
  return FEATURE_LABELS[key] ?? null;
}

/**
 * Capabilities a plan is sold with that the product does not have yet.
 *
 * Keyed by capability, never by plan. Each is shown under « Bientôt
 * disponible », apart from what a plan includes today: a check mark beside
 * something nobody can use is how a price list starts promising what the
 * product does not do. A key leaves this set the day its capability ships,
 * and every plan that carries it becomes true at once.
 */
const COMING_SOON: ReadonlySet<string> = new Set([
  // One location per establishment today; the dashboard edits that one.
  'multi_location',
  'advanced_workflows',
  'advanced_integrations',
]);

/** Sold, and not built yet. */
export function isComingSoon(key: string): boolean {
  return COMING_SOON.has(key);
}

/** Digits as a French reader expects them: 1 000, not 1000. */
function count(value: number): string {
  return new Intl.NumberFormat(LOCALE).format(value);
}

/**
 * A limit, in words.
 *
 * `null` is unlimited and `0` is none, and they are not the same sentence —
 * conflating them is how a plan that forbids transcript retention gets
 * advertised as retaining them for ever.
 */
export function limitLabel(key: string, value: number | null): string | null {
  switch (key) {
    case 'included_minutes':
      return value === null ? 'Minutes incluses sans limite' : `${count(value)} minutes incluses`;
    case 'max_locations':
      // Never a line of its own. One establishment is the default rather than
      // a selling point, and more than one is `multi_location` — not built
      // yet, and said as such. A count beside it put « 3 établissements » on
      // a card that can manage one.
      return null;
    case 'max_phone_numbers':
      // "numéros", not "numérox". The plural was wrong on the page before a
      // screenshot caught it, which is an argument for looking at the result.
      return value === null ? null : `${count(value)} numéro${value > 1 ? 's' : ''}`;
    case 'max_concurrent_calls':
      return value === null
        ? null
        : `${count(value)} appel${value > 1 ? 's' : ''} simultané${value > 1 ? 's' : ''}`;
    case 'max_staff':
      // « Plusieurs collaborateurs » is what the grid sells: the people the
      // assistant can book with. Accounts on the dashboard are not the same
      // thing, and a card saying both read as a contradiction.
      if (value === null) return 'Collaborateurs sans limite';
      return value === 1 ? '1 collaborateur' : `Jusqu’à ${count(value)} collaborateurs`;
    case 'max_users':
      return null;
    case 'transcript_retention_days':
      // Zero is a real answer and the default one: transcripts are not kept
      // unless the establishment asks. Saying "0 jours de conservation" sounds
      // like a missing feature; saying what it is, is the honest version.
      return value === 0 ? 'Aucune conservation de transcription' : null;
    default:
      return null;
  }
}

/**
 * A plan's quotas as lines on a card, always in the same order.
 *
 * The order is the reader's — minutes, then the people, then the lines — not
 * the order a database happens to return rows in, so the card on the site and
 * the card in the dashboard read the same.
 */
const LIMIT_ORDER = [
  'included_minutes',
  'max_staff',
  'max_users',
  'max_locations',
  'max_phone_numbers',
  'max_concurrent_calls',
  'transcript_retention_days',
];

export function limitLines(limits: Readonly<Record<string, number | null | undefined>>): string[] {
  const rank = (key: string): number => {
    const index = LIMIT_ORDER.indexOf(key);
    return index === -1 ? LIMIT_ORDER.length : index;
  };
  return Object.entries(limits)
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([key, value]) => (value === undefined ? null : limitLabel(key, value)))
    .filter((label): label is string => label !== null);
}

/**
 * The price a visitor can start at, for the top of the page.
 *
 * The cheapest monthly price among the plans on sale, and whether every one of
 * them comes without a setup fee — read from the catalogue like the board
 * further down, so the line under the hero's buttons and the board cannot
 * disagree. Null when nothing has a monthly price, and the line is not shown.
 */
export function entryPrice(): { readonly monthly: string; readonly noSetupFee: boolean } | null {
  let cheapest: number | null = null;
  let noSetupFee = true;
  for (const plan of LAUNCH_PLANS) {
    if (!plan.isPublic) continue;
    const version = plan.versions.find((candidate) => candidate.status === 'active');
    for (const price of version?.prices ?? []) {
      if (price.kind === 'setup' && price.amountMinor > 0) noSetupFee = false;
      if (price.kind === 'base' && price.billingInterval === 'monthly') {
        cheapest = cheapest === null ? price.amountMinor : Math.min(cheapest, price.amountMinor);
      }
    }
  }
  return cheapest === null ? null : { monthly: euros(cheapest), noSetupFee };
}

export function publicPlans(): readonly PublicPlan[] {
  return LAUNCH_PLANS.filter((plan) => plan.isPublic)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((plan) => {
      // The version currently on sale. A plan keeps older versions so that
      // nobody is silently repriced (ADR-0016), and the site advertises the
      // one a new customer would buy.
      const version = plan.versions.find((candidate) => candidate.status === 'active');
      const prices = version?.prices ?? [];
      const monthly = prices.find((p) => p.kind === 'base' && p.billingInterval === 'monthly');
      const annual = prices.find((p) => p.kind === 'base' && p.billingInterval === 'annual');
      const setup = prices.find((p) => p.kind === 'setup');

      const limits = limitLines(version?.limits ?? {});

      const labelled = (keys: readonly string[]): string[] =>
        keys
          .map((key) => FEATURE_LABELS[key])
          .filter((label): label is string => label !== undefined);
      const granted = version?.features ?? [];
      const features = labelled(granted.filter((key) => !isComingSoon(key)));
      const upcoming = labelled(granted.filter((key) => isComingSoon(key)));

      const overage = (version?.overageRates ?? []).find(
        (rate) => rate.usageType === 'VOICE_MINUTES',
      );

      return {
        code: plan.code,
        name: plan.name,
        summary: plan.description,
        monthly: monthly === undefined ? null : euros(monthly.amountMinor),
        annualPerMonth: annual === undefined ? null : euros(Math.round(annual.amountMinor / 12)),
        setupFee: setup === undefined ? null : euros(setup.amountMinor),
        overagePerMinute: overage === undefined ? null : euros(overage.unitAmountMinor),
        features: [...limits, ...features],
        upcoming,
      };
    });
}
